import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useLazyViewGlobalConfigurationQuery } from "../../redux/api/user/userAPI";
import { selectTheme, setTheme } from "../../redux/features/theme/theme-slice";
import { ViewUserConfigurationResponseDTO } from "../../shared/oversight-core/dtos/response-dtos/view-user-configuration-response-dto";
import { EConfigurations } from "../../shared/oversight-core/enums/configurations";
import { AppRoute } from "../../shared/oversight-core/interfaces/app-routes";
import { IBrandPriority } from "../../shared/oversight-core/interfaces/brand-priority";
import { Configurations } from "../../shared/oversight-core/interfaces/configurations";
import SettingItem from "../../shared/oversight-core/shared-components/setting-item/setting-item";
import AppSwitch from "../../shared/oversight-core/ui-elements/app-switch/app-switch";
import ArrowButton from "../../shared/oversight-core/ui-elements/arrow-button/arrow-button";
import AppButton from "../../shared/oversight-core/ui-elements/buttons/app-button/app-button";
import MaterialIcon from "../../shared/oversight-core/ui-elements/material-icon/material-icon";
import SpinnerModal from "../../shared/oversight-core/ui-elements/spinner/spinner";
import { configurationDescription } from "../../shared/oversight-core/utils/configuration-description";
import translate from "../../shared/oversight-core/utils/translate";
import BrandPriorityChangeModal from "../build-system/components/brand-priority-change-modal/brand-priority-change-modal";
import styles from "./public-settings.module.scss";

interface IExtendedBrandPriority extends IBrandPriority {
  id: number;
}

const PublicSettings = () => {
  const theme = useSelector(selectTheme);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [selectedItem, setSelectedItem] = useState<{
    itemName: string;
    id: number;
  }>({
    itemName: "Solar Recommendation",
    id: 1,
  });
  const [configurations, setConfigurations] = useState<Configurations>(
    {} as Configurations
  );
  const [showBrandPriorityModal, setShowBrandPriorityModal] = useState(false);
  const [brands, setBrands] = useState<IExtendedBrandPriority[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [triggerGlobalConfiguration, { isFetching }] =
    useLazyViewGlobalConfigurationQuery();

  useEffect(() => {
    triggerGlobalConfiguration()
      .unwrap()
      .then((res: ViewUserConfigurationResponseDTO) => {
        const globalConfigurations = localStorage.getItem(
          "globalConfigurations"
        );

        if (globalConfigurations) {
          const parsedGlobalConfigurations: Configurations =
            JSON.parse(globalConfigurations);

          setConfigurations(parsedGlobalConfigurations);

          return;
        }

        setConfigurations(res?.configurations?.configurations || ({} as Configurations));
      })
      .catch(() => {
        setConfigurations({} as Configurations);
      });
  }, [triggerGlobalConfiguration]);

  useEffect(() => {
    const list = configurations?.BRAND_PRIORITY_LIST;
    setBrands(
      Array.isArray(list)
        ? list.map(
            (bp, index): IExtendedBrandPriority => ({
              ...bp,
              id: index,
            })
          )
        : []
    );
  }, [configurations]);

  const onPreferenceChange = (
    configuration: EConfigurations,
    newValue: boolean | IBrandPriority[]
  ) => {
    const updatedConfigurations = Object.fromEntries(
      Object.entries(configurations || {}).map(([key, value]) => [
        key,
        key === configuration ? newValue : value,
      ])
    );

    setConfigurations(updatedConfigurations as Configurations);
  };

  const onSaveConfiguration = async () => {
    setIsLoading(true);

    const globalConfigurations: Configurations = configurations;

    localStorage.setItem(
      "globalConfigurations",
      JSON.stringify(globalConfigurations)
    );

    setTimeout(() => {
      setIsLoading(false);
    }, 1500);
  };

  return (
    <div className="p-3">
      <div className={`${styles.container} px-2 px-lg-4 position-relative`}>
        <Row>
          <Col
            className={`col-3 col-xl-2 ${styles.settingItemContainer} d-none d-lg-block`}
          >
            <div className={styles.settingItems}>
              <SettingItem
                items={[
                  {
                    itemName: "Solar Recommendation",
                    id: 1,
                  },
                  { itemName: "Theme", id: 2 },
                ]}
                onChange={(item) => setSelectedItem(item)}
                selectedItemId={selectedItem.id}
              />
            </div>
          </Col>
          <Col>
            <div className={styles.settings}>
              <Row className="align-items-center">
                <Col>
                  <Row className="align-items-center row-cols-auto mx-0">
                    <Col>
                      <ArrowButton
                        onBack={() => navigate(AppRoute.PUBLIC_DESIGN_SYSTEM)}
                      />
                    </Col>
                    <Col className="font-weight-500 font-size-24 title-2">
                      {selectedItem.id === 1
                        ? `Solar Recommendation Engine Rules`
                        : selectedItem.itemName}
                    </Col>
                  </Row>
                </Col>
                {selectedItem.id === 1 && (
                  <Col className="col-auto">
                    <AppButton
                      text="Save"
                      className="px-5 font-size-14"
                      onClick={onSaveConfiguration}
                    />
                  </Col>
                )}
              </Row>
              <div className={styles.hr}></div>
              {selectedItem.id === 1 && (
                <>
                  <Row>
                    <Col className="title-3 text-size-16 text-weight-500 mt-3">
                      System Design Preferences
                    </Col>
                  </Row>
                  {Object.entries(configurations || {}).map(
                    ([configuration, value]) => {
                      if (
                        configuration === EConfigurations.BRAND_PRIORITY_LIST
                      ) {
                        return null;
                      }

                      return (
                        <Row key={configuration} className="my-4">
                          {configuration ===
                            EConfigurations.OPTIMIZE_FOR_COST && (
                            <Row>
                              <Col className="title-3 text-size-16 text-weight-500 my-3">
                                Performance Preferences
                              </Col>
                            </Row>
                          )}
                          <Row className="align-items-center">
                            <Col className="col-auto title-2 font-weight-500 pe-0">
                              {translate(configuration)}
                            </Col>
                            <Col className="col-auto">
                              <AppSwitch
                                isOn={typeof value === "boolean" && value}
                                onSwitch={(value: boolean) => {
                                  onPreferenceChange(
                                    configuration as EConfigurations,
                                    value
                                  );
                                }}
                                size="sm"
                              />
                            </Col>
                          </Row>
                          <Row className="my-2">
                            <Col className="text-color-2 font-size-12">
                              {configurationDescription(
                                configuration as EConfigurations
                              )}
                            </Col>
                          </Row>
                          {configuration === EConfigurations.BRAND_PRIORITY && (
                            <Row>
                              <Col
                                className="text-color-2 font-size-12 text-decoration-underline cursor-pointer"
                                onClick={() => {
                                  setShowBrandPriorityModal(true);
                                }}
                              >
                                Change Brand Priority
                              </Col>
                            </Row>
                          )}
                        </Row>
                      );
                    }
                  )}
                </>
              )}
              {selectedItem.id === 2 && (
                <Row className="row-cols-auto gap-3 mt-4 mx-0">
                  <Col
                    className={`theme-card rounded-3 py-4 px-5 cursor-pointer border border-2 ${
                      theme === "dark" ? `border-primary` : `border-none`
                    } ${styles.themeCard}`}
                    onClick={() => dispatch(setTheme("dark"))}
                  >
                    <Row className="justify-content-center">
                      <Col className="col-auto">
                        <MaterialIcon
                          icon="dark_mode"
                          color="var(--color-theme-icon)"
                        />
                      </Col>
                    </Row>
                    <Row className="mt-1">
                      <Col className="title-2">Dark Mode</Col>
                    </Row>
                    {theme === "dark" && (
                      <Row className="justify-content-center mt-2">
                        <Col className="col-auto text-white bg-primary rounded-4">
                          Active
                        </Col>
                      </Row>
                    )}
                  </Col>
                  <Col
                    className={`theme-card rounded-3 py-4 px-5 cursor-pointer border border-2 ${
                      theme === "light" ? `border-primary` : `border-none`
                    } ${styles.themeCard}`}
                    onClick={() => dispatch(setTheme("light"))}
                  >
                    <Row className="justify-content-center">
                      <Col className="col-auto">
                        <MaterialIcon
                          icon="light_mode"
                          color="var(--color-theme-icon)"
                        />
                      </Col>
                    </Row>
                    <Row className="mt-1">
                      <Col className="title-2">Light Mode</Col>
                    </Row>
                    {theme === "light" && (
                      <Row className="justify-content-center mt-2">
                        <Col className="col-auto text-white bg-primary rounded-4">
                          Active
                        </Col>
                      </Row>
                    )}
                  </Col>
                  <Col
                    className={`theme-card rounded-3 py-4 px-5 cursor-pointer border border-2 ${
                      theme === "system" ? `border-primary` : `border-none`
                    } ${styles.themeCard}`}
                    onClick={() => dispatch(setTheme("system"))}
                  >
                    <Row className="justify-content-center">
                      <Col className="col-auto">
                        <MaterialIcon
                          icon="desktop_windows"
                          color="var(--color-theme-icon)"
                        />
                      </Col>
                    </Row>
                    <Row className="mt-1">
                      <Col className="title-2">System Default</Col>
                    </Row>
                    {theme === "system" && (
                      <Row className="justify-content-center mt-2">
                        <Col className="col-auto text-white bg-primary rounded-4">
                          Active
                        </Col>
                      </Row>
                    )}
                  </Col>
                </Row>
              )}
            </div>
          </Col>
        </Row>
        <BrandPriorityChangeModal
          show={showBrandPriorityModal}
          onClose={() => setShowBrandPriorityModal(false)}
          onCancel={() => setShowBrandPriorityModal(false)}
          onConfirm={() => {
            onPreferenceChange(
              EConfigurations.BRAND_PRIORITY_LIST,
              brands.map((brand, index) => {
                return {
                  brand: brand.brand,
                  priority: index + 1,
                };
              })
            );
            setShowBrandPriorityModal(false);
          }}
          brands={brands}
          setBrands={setBrands}
        />
        <SpinnerModal show={isFetching || isLoading} />
      </div>
    </div>
  );
};

export default PublicSettings;
